import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, isValidElement, cloneElement } from 'react'
import {
  Maximize2,
  Minimize2,
  RotateCcw,
  Target,
  ZoomIn,
  ZoomOut,
  Download,
} from 'lucide-react'
import {
  computeEffectiveScale,
  computeFitScale,
  computeZoomedContentSize,
  GRAPH_PAN_THRESHOLD_PX,
  VIEWPORT_DEFAULT_HEIGHT_PX,
  VIEWPORT_MIN_HEIGHT_PX,
  computeButtonZoomViewUpdate,
  computeWheelZoomViewUpdate,
  shouldShowGraphZoomTip,
} from './graphViewport'
import { Loader2 } from 'lucide-react'

export interface GraphViewportProps {
  svgWidth: number
  svgHeight: number
  /** Changes reset zoom/pan (e.g. profile root CVR — not on expand merges). */
  graphKey: string
  children: React.ReactNode
  /** Slide-in panel rendered inside the viewport shell (left edge). */
  overlay?: React.ReactElement<{ viewportRef?: React.RefObject<HTMLDivElement | null> }>
  /** Restore initial graph data (expand merges undone) — like refreshing the page. */
  onResetGraph?: () => void
  /** Export the current graph as a standalone SVG file. */
  onDownloadSvg?: () => void
  /** Shown while an expand-up/down request is in flight. */
  expandStatus?: string | null
}

interface PanSession {
  pointerId: number
  startX: number
  startY: number
  originX: number
  originY: number
  moved: boolean
}

function isGraphInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  if (target.closest('[data-graph-control]')) return true
  if (target.closest('svg [role="button"]')) return true
  if (target.closest('[data-graph-panel]')) return true
  if (target.closest('[data-watchlist-menu]')) return true
  return false
}

/** Wheel zoom is allowed over graph nodes; only chrome suppresses it. */
function isWheelZoomSuppressedTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  if (target.closest('[data-graph-control]')) return true
  if (target.closest('[data-graph-panel]')) return true
  if (target.closest('[data-watchlist-menu]')) return true
  return false
}

const GraphViewport: React.FC<GraphViewportProps> = ({
  svgWidth,
  svgHeight,
  graphKey,
  children,
  overlay,
  onResetGraph,
  onDownloadSvg,
  expandStatus,
}) => {
  const shellRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  // Gates the first paint: until the container is measured we cannot compute a
  // correct fit-scale, so a wide graph would briefly render at full pixel width
  // and be clipped at the viewport edge. Hide the canvas until measured.
  const [hasMeasured, setHasMeasured] = useState(false)
  const [userZoom, setUserZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const panSession = useRef<PanSession | null>(null)
  const viewStateRef = useRef({
    pan: { x: 0, y: 0 },
    userZoom: 1,
    containerWidth: 0,
    fitScale: 1,
    svgWidth,
    svgHeight,
  })

  const fitScale = computeFitScale(containerWidth, svgWidth)
  const effectiveScale = computeEffectiveScale(fitScale, userZoom)
  const showZoomTip = hasMeasured && shouldShowGraphZoomTip(effectiveScale)
  const zoomedContent = computeZoomedContentSize(svgWidth, svgHeight, effectiveScale)

  const centerOffsetX = Math.max(0, (containerWidth - zoomedContent.width) / 2)
  const centerOffsetY = Math.max(
    0,
    ((isFullscreen ? scrollRef.current?.clientHeight : VIEWPORT_DEFAULT_HEIGHT_PX) ?? VIEWPORT_DEFAULT_HEIGHT_PX) -
      zoomedContent.height,
  ) / 2

  viewStateRef.current = {
    pan,
    userZoom,
    containerWidth,
    fitScale,
    svgWidth,
    svgHeight,
  }

  useEffect(() => {
    setUserZoom(1)
    setPan({ x: 0, y: 0 })
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0
      scrollRef.current.scrollTop = 0
    }
  }, [graphKey])

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return

    // Robust width read. clientWidth can momentarily be 0 (mounted in a tab
    // that was just shown, layout not flushed yet). A 0 width would make
    // computeFitScale fall back to 1:1 and render a wide graph at full pixel
    // width — which then clips at the frame. Fall back to the outer shell and
    // finally the window so we never feed 0 into the fit-scale on a real page.
    const readWidth = (): number => {
      const own = el.clientWidth || Math.round(el.getBoundingClientRect().width)
      if (own > 0) return own
      const shell = shellRef.current
      const shellW = shell
        ? shell.clientWidth || Math.round(shell.getBoundingClientRect().width)
        : 0
      if (shellW > 0) return shellW
      return typeof window !== 'undefined' ? window.innerWidth : 0
    }

    const measure = () => {
      setContainerWidth(readWidth())
      setHasMeasured(true)
    }

    measure()

    let ro: ResizeObserver | null = null
    try {
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => measure())
        ro.observe(el)
      }
    } catch {
      ro = null
    }

    if (!ro) {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }

    return () => ro.disconnect()
  }, [graphKey])

  useEffect(() => {
    const onFullscreenChange = () => {
      const fs = document.fullscreenElement === shellRef.current
      setIsFullscreen(fs)
      if (fs) {
        requestAnimationFrame(() => {
          const el = scrollRef.current
          if (el) setContainerWidth(el.clientWidth)
        })
      }
    }
    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el || !isFullscreen) return

    const onWheel = (e: WheelEvent) => {
      if (isWheelZoomSuppressedTarget(e.target)) return
      e.preventDefault()

      const rect = el.getBoundingClientRect()
      const mouseX = e.clientX - rect.left + el.scrollLeft
      const mouseY = e.clientY - rect.top + el.scrollTop
      const { pan: panNow, userZoom, containerWidth, fitScale, svgWidth, svgHeight } =
        viewStateRef.current

      const update = computeWheelZoomViewUpdate({
        mouseX,
        mouseY,
        pan: panNow,
        userZoom,
        fitScale,
        svgWidth,
        svgHeight,
        containerWidth,
        containerHeight: el.clientHeight,
        deltaY: e.deltaY,
      })
      if (!update) return
      setUserZoom(update.userZoom)
      setPan(update.pan)
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [isFullscreen])

  const resetView = useCallback(() => {
    setUserZoom(1)
    setPan({ x: 0, y: 0 })
  }, [])

  const applyButtonZoom = useCallback(
    (direction: 'in' | 'out') => {
      const frame = scrollRef.current
      const shell = shellRef.current
      const frameWidth = (() => {
        const own =
          frame?.clientWidth ||
          (frame ? Math.round(frame.getBoundingClientRect().width) : 0)
        if (own > 0) return own
        if (containerWidth > 0) return containerWidth
        const shellW =
          shell?.clientWidth ||
          (shell ? Math.round(shell.getBoundingClientRect().width) : 0)
        if (shellW > 0) return shellW
        return typeof window !== 'undefined' ? window.innerWidth : 0
      })()
      const frameHeight =
        frame?.clientHeight ||
        (frame ? Math.round(frame.getBoundingClientRect().height) : 0) ||
        VIEWPORT_DEFAULT_HEIGHT_PX
      if (frameWidth <= 0 || frameHeight <= 0) return

      const update = computeButtonZoomViewUpdate({
        direction,
        pan,
        userZoom,
        fitScale,
        svgWidth,
        svgHeight,
        containerWidth: frameWidth,
        containerHeight: frameHeight,
      })
      if (!update) return
      setUserZoom(update.userZoom)
      setPan(update.pan)
    },
    [pan, userZoom, fitScale, svgWidth, svgHeight, containerWidth],
  )

  const handleResetGraph = useCallback(() => {
    resetView()
    onResetGraph?.()
  }, [resetView, onResetGraph])

  const toggleFullscreen = useCallback(() => {
    const el = shellRef.current
    if (!el) return
    if (document.fullscreenElement) {
      void document.exitFullscreen()
    } else {
      void el.requestFullscreen()
    }
  }, [])

  const endPan = useCallback((e: React.PointerEvent) => {
    const session = panSession.current
    if (!session || session.pointerId !== e.pointerId) return
    panSession.current = null
    setIsDragging(false)
    try {
      scrollRef.current?.releasePointerCapture(e.pointerId)
    } catch {
      /* already released */
    }
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return
      if (isGraphInteractiveTarget(e.target)) return
      panSession.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        originX: pan.x,
        originY: pan.y,
        moved: false,
      }
    },
    [pan.x, pan.y],
  )

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const session = panSession.current
    if (!session || session.pointerId !== e.pointerId) return
    const dx = e.clientX - session.startX
    const dy = e.clientY - session.startY
    if (!session.moved && Math.hypot(dx, dy) < GRAPH_PAN_THRESHOLD_PX) return
    if (!session.moved) {
      session.moved = true
      setIsDragging(true)
      try {
        scrollRef.current?.setPointerCapture(e.pointerId)
      } catch {
        /* jsdom / unsupported */
      }
    }
    setPan({ x: session.originX + dx, y: session.originY + dy })
  }, [])

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      endPan(e)
    },
    [endPan],
  )

  const controlBtn =
    'p-1.5 rounded border border-rule bg-paper text-ink-500 hover:text-ink hover:bg-paper-200 transition-colors disabled:opacity-40 disabled:pointer-events-none'

  const viewportFrameStyle: React.CSSProperties | undefined = isFullscreen
    ? undefined
    : {
        height: VIEWPORT_DEFAULT_HEIGHT_PX,
        minHeight: VIEWPORT_MIN_HEIGHT_PX,
        maxHeight: '85vh',
      }

  return (
    <div
      ref={shellRef}
      className={`relative rounded border border-rule bg-paper ${isFullscreen ? 'h-screen flex flex-col' : ''}`}
      data-testid="ownership-graph-viewport"
      data-user-zoom={userZoom}
      data-fit-scale={fitScale}
      data-effective-scale={effectiveScale}
    >
      <div
        className="absolute top-2 right-2 z-10 flex items-center gap-1"
        data-graph-control
      >
        <button
          type="button"
          className={controlBtn}
          aria-label="Zoom ind"
          data-testid="ownership-graph-zoom-in"
          onClick={() => applyButtonZoom('in')}
        >
          <ZoomIn size={16} strokeWidth={1.75} />
        </button>
        <button
          type="button"
          className={controlBtn}
          aria-label="Zoom ud"
          data-testid="ownership-graph-zoom-out"
          onClick={() => applyButtonZoom('out')}
        >
          <ZoomOut size={16} strokeWidth={1.75} />
        </button>
        <button
          type="button"
          className={controlBtn}
          aria-label="Nulstil zoom og pan"
          title="Nulstil zoom og pan"
          data-testid="ownership-graph-zoom-reset"
          onClick={resetView}
        >
          <Target size={16} strokeWidth={1.75} />
        </button>
        {onResetGraph && (
          <button
            type="button"
            className={controlBtn}
            aria-label="Nulstil graf"
            title="Nulstil graf"
            data-testid="ownership-graph-reset"
            onClick={handleResetGraph}
          >
            <RotateCcw size={16} strokeWidth={1.75} />
          </button>
        )}
        {onDownloadSvg && (
          <button
            type="button"
            className={controlBtn}
            aria-label="Download graf som SVG"
            title="Download graf som SVG"
            data-testid="ownership-graph-download-svg"
            onClick={onDownloadSvg}
          >
            <Download size={16} strokeWidth={1.75} />
          </button>
        )}
        <button
          type="button"
          className={controlBtn}
          aria-label={isFullscreen ? 'Afslut fuld skærm' : 'Fuld skærm'}
          title={isFullscreen ? 'Afslut fuld skærm' : 'Fuld skærm'}
          data-testid="ownership-graph-fullscreen"
          onClick={toggleFullscreen}
        >
          {isFullscreen ? (
            <Minimize2 size={16} strokeWidth={1.75} />
          ) : (
            <Maximize2 size={16} strokeWidth={1.75} />
          )}
        </button>
      </div>

      {showZoomTip && (
        <p
          role="status"
          data-testid="ownership-graph-zoom-tip"
          className="absolute top-2 left-1/2 z-10 max-w-[min(28rem,calc(100%-5rem))] -translate-x-1/2 rounded-sm border border-rule bg-paper/95 px-3 py-2 text-center text-[11px] leading-snug text-ink-600 shadow-sm pointer-events-none"
        >
          <span className="font-medium text-ink">Tip:</span>{' '}
          Denne graf er meget stor. Zoom ind eller vælg fuldskærmsvisning for bedre at se
        </p>
      )}

      {expandStatus && (
        <p
          role="status"
          data-testid="ownership-graph-expand-status"
          className="absolute top-12 left-1/2 z-10 flex max-w-[min(24rem,calc(100%-2rem))] -translate-x-1/2 items-center gap-2 rounded-full border border-rule bg-paper/95 px-3 py-1.5 text-[11px] text-ink-600 shadow-sm pointer-events-none"
        >
          <Loader2 size={14} strokeWidth={2} className="shrink-0 animate-spin text-signal" aria-hidden />
          <span>{expandStatus}</span>
        </p>
      )}

      {overlay && (
        <div
          className="absolute top-3 left-3 z-20 bottom-3 max-w-[min(360px,85%)] pointer-events-none overflow-hidden"
          data-testid="ownership-graph-panel-slot"
        >
          {isValidElement(overlay)
            ? cloneElement(overlay, { viewportRef: shellRef })
            : overlay}
        </div>
      )}

      <div
        ref={scrollRef}
        style={viewportFrameStyle}
        className={`relative overflow-hidden w-full select-none ${
          isFullscreen ? 'flex-1 min-h-0' : ''
        } ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        data-testid="ownership-graph-viewport-frame"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {/* The frame is the ONLY clip boundary. Drag-pan translates this layer
            within the frame; content moved past an edge is hidden but fully
            recoverable by panning back — never hard-clipped mid-canvas. */}
        <div
          className="absolute left-0 top-0 origin-top-left"
          style={{
            width: svgWidth,
            height: svgHeight,
            transform: `translate(${centerOffsetX + pan.x}px, ${centerOffsetY + pan.y}px) scale(${effectiveScale})`,
            visibility: hasMeasured ? 'visible' : 'hidden',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

export default GraphViewport
